import {
    $generateHtmlFromNodes,
    $generateNodesFromDOM,
    $getRoot,
    editorTheme,
    HeadingNode,
    HistoryPlugin,
    LexicalComposer,
    LexicalEditor,
    LexicalErrorBoundary,
    LinkNode,
    LinkPlugin,
    ListItemNode,
    ListNode,
    ListPlugin,
    OnChangePlugin,
    ParagraphNode,
    RichContentEditable,
    RichTextPlugin,
    ToolbarPlugin,
    ToolbarSeparator,
} from '@talentmesh/rte';
import React, { useCallback } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import UIStrings from '@/Utils/UIStrings';
import PlaceholderPlugins, { Placeholder } from '../../components/RTE/PlaceholderPlugins';
import { useLinkedInMessageOutreach } from '@/Pages/Candidates/AiSearch/api/useOutreachCandidate';
import { LinkedInMessageLimit, useLinkedInMessageFormContext } from './useLinkedInMessageForm';
import { cn } from '@/lib/utils';
import { FieldError } from '@/Components/shadcn/field';

const linkedinInmailPlaceholders = {
    firstName: '{firstName}',
    lastName: '{lastName}',
    fullName: '{fullName}',
};

const placeholders: Placeholder[] = [
    { label: UIStrings.FirstName, value: linkedinInmailPlaceholders.firstName },
    { label: UIStrings.LastName, value: linkedinInmailPlaceholders.lastName },
    { label: UIStrings.FullName, value: linkedinInmailPlaceholders.fullName },
];

const LinkedInMessageEditor = () => {
    const { data } = useLinkedInMessageOutreach();
    const form = useLinkedInMessageFormContext();
    const { defaultValues } = form.formState;
    const { htmlTextLength } = useWatch({ control: form.control });

    const $prepopulatedRichText = useCallback(
        (editor: LexicalEditor) => {
            if (!defaultValues?.messageHtml) {
                return;
            }

            const parser = new DOMParser();
            const dom = parser.parseFromString(defaultValues.messageHtml, 'text/html');
            const root = $getRoot();
            const nodes = $generateNodesFromDOM(editor, dom);

            root.append(...nodes);
        },
        [data?.message, defaultValues?.messageHtml],
    );

    const handleEditorOnChange = useDebounceCallback<Parameters<typeof OnChangePlugin>['0']['onChange']>(
        (editorState, editor) => {
            editorState.read(() => {
                const length = editor.getRootElement()?.textContent?.length || 0;
                const value = $generateHtmlFromNodes(editor);
                form.setValue('messageHtml', value, { shouldDirty: true, shouldValidate: true });
                form.setValue('htmlTextLength', length, { shouldDirty: true, shouldValidate: true });
            });
        },
        10,
    );

    return (
        <>
            <LexicalComposer
                initialConfig={{
                    editorState: $prepopulatedRichText,
                    namespace: 'Outreach candidate linkedin message editor',
                    onError: ({ message }) => toast.error(message),
                    theme: editorTheme,
                    nodes: [LinkNode, HeadingNode, ParagraphNode, ListNode, ListItemNode],
                }}
            >
                <HistoryPlugin />
                <ToolbarPlugin features={['headingStyle', 'bold', 'italic', 'ol', 'ul']}>
                    <ToolbarSeparator />
                    <div className="ml-2 flex gap-2">
                        <PlaceholderPlugins placeholders={placeholders} />
                    </div>
                </ToolbarPlugin>
                <OnChangePlugin ignoreHistoryMergeTagChange ignoreSelectionChange onChange={handleEditorOnChange} />
                <ListPlugin />
                <LinkPlugin />
                <RichTextPlugin
                    ErrorBoundary={LexicalErrorBoundary}
                    contentEditable={
                        <RichContentEditable
                            className={cn(
                                'border-border h-full border',
                                form.formState.errors.htmlTextLength?.message ? 'border-destructive' : '',
                            )}
                        />
                    }
                />
            </LexicalComposer>
            <div className="mt-1 flex justify-between gap-2">
                <FieldError>{form.formState.errors.htmlTextLength?.message}</FieldError>
                <span
                    className={cn(
                        'ml-auto place-self-end',
                        form.formState.errors.htmlTextLength?.message
                            ? 'text-destructive text-xs'
                            : 'text-muted-foreground text-xs',
                    )}
                >
                    {htmlTextLength} / {LinkedInMessageLimit}
                </span>
            </div>
        </>
    );
};

export default LinkedInMessageEditor;
